import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar, Target, Award, Users, CheckCircle, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { performanceApi, employeeApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useManagerViewPermission } from '../../hooks/useManagerViewPermission';
import EmployeeGoalsView from './EmployeeGoalsView';
import ManagerTeamView from './ManagerTeamView';
import { cn } from '../../lib/utils';

const GoalsAndReviews: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Get current user from auth context
  const { user, isManager, isAdmin } = useAuth();
  const { isUserView, canViewTeamPerformance } = useManagerViewPermission();
  const currentUserId = user?.id || '';
  const canViewTeamOverview = (isManager || isAdmin) && canViewTeamPerformance;

  // Generate year options (current year ± 2 years)
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // Fetch data for selected year
  const { data: goals = [], isLoading: goalsLoading } = useQuery({
    queryKey: ['performanceGoals', selectedYear],
    queryFn: () => performanceApi.goals.getAll({ year: selectedYear }),
  });

  const { data: reviews = [], isLoading: reviewsLoading } = useQuery({
    queryKey: ['performanceReviews', selectedYear],
    queryFn: () => performanceApi.reviews.getAll({ year: selectedYear }),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  const isLoading = goalsLoading || reviewsLoading;

  // Calculate metrics for overview
  const myGoals = Array.isArray(goals) ? goals.filter((g: any) => g.employee_id === currentUserId) : [];
  const myReviews = Array.isArray(reviews) ? reviews.filter((r: any) => r.employee_id === currentUserId) : [];

  const goalsCompleted = myGoals.filter((g: any) => g.status === 'COMPLETED').length;
  const reviewsCompleted = myReviews.filter((r: any) => r.status === 'COMPLETED').length;
  const reviewsPending = myReviews.filter((r: any) => r.status === 'PENDING' || r.status === 'IN_PROGRESS').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 font-bold text-slate-400">Loading performance data...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
     

      {/* Header with Year Selector */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Track your performance goals and reviews</h2>
        </div>
        <div className="flex items-center gap-3 self-start md:self-auto">
          <div className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-1.5 shadow-sm backdrop-blur-sm">
            <Calendar className="h-4 w-4 text-slate-400 ml-2" />
            <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
              <SelectTrigger className="w-28 border-none bg-transparent focus:ring-0 focus:ring-offset-0 font-bold text-sm h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-100 dark:border-slate-800">
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year.toString()} className="font-bold text-sm">
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Total Goals</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{myGoals.length}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Assigned Goals</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-500 shadow-sm">
                <Target className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Completed</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{goalsCompleted}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Achieved Goals</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 shadow-sm">
                <CheckCircle className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Reviews</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{reviewsCompleted}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Completed Reviews</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-purple-50 dark:bg-purple-900/20 text-purple-500 shadow-sm">
                <Award className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Pending</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{reviewsPending}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Awaiting Action</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-900/20 text-amber-500 shadow-sm">
                <Clock className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Employee vs Manager View */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-1 mb-6 w-fit shadow-sm backdrop-blur-sm rounded-xl">
          <TabsTrigger 
            value="overview" 
            className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-lg shadow-none"
          >
            My Performance
          </TabsTrigger>
          {canViewTeamOverview && (
            <TabsTrigger 
              value="team"
              className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-lg shadow-none"
            >
              Team Overview
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="space-y-6 outline-none">
          <EmployeeGoalsView 
            year={selectedYear} 
            employeeId={currentUserId}
            goals={myGoals}
            reviews={myReviews}
          />
        </TabsContent>

        {canViewTeamOverview && (
          <TabsContent value="team" className="space-y-6 outline-none">
            <ManagerTeamView 
              year={selectedYear} 
              managerId={currentUserId}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default GoalsAndReviews;
